/**
 * Agent Debug Feeds — feed catalog.
 *
 * The dialog's "Available Feeds" list is this catalog intersected with the
 * feeds actually emitting for the current call (dynamic discovery in the
 * stream hook). Unknown `${source}/${feed}` pairs still appear, rendered with
 * a generic label and the default JSON-log renderer — so a newly-instrumented
 * emitter shows up without a frontend change.
 *
 * Phase 1 ships the default JsonLogRenderer for every feed. Custom renderers
 * (RAG chunk inspector, orchestration-tree viewer, persona viewer with
 * hidden-truth flagged, tools threaded by turnId, media grid) are Phase 4 and
 * can be slotted in here incrementally — nothing is blocked on them.
 *
 * TODO(Phase 5): "Sessions" feed — attach to any live call (cross-call
 * enumeration); not implemented in v1 (v1 scopes to the active-call page).
 *
 * See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.4 / §6.2.
 */
import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Database,
  ListFilter,
  FileText,
  UserCog,
  Sparkles,
  Image as ImageIcon,
  ScrollText,
  Wrench,
  Activity,
  GitBranch,
  MessageSquare,
  Layers,
  Megaphone,
} from 'lucide-react';
import type { AgentDebugEvent } from '@/lib/debug/types';
import { JsonLogRenderer, type FeedRendererProps } from './renderers/JsonLogRenderer';
import { PersonaRenderer } from './renderers/PersonaRenderer';
import { RagRenderer } from './renderers/RagRenderer';
import { ToolsRenderer } from './renderers/ToolsRenderer';
import { MediaRenderer } from './renderers/MediaRenderer';

export type { FeedRendererProps };

export interface FeedDescriptor {
  id: string; // `${source}/${feed}`
  source: string;
  feed: string;
  label: string;
  icon: LucideIcon;
  kinds: string[]; // informational (which event kinds belong to this feed)
  Renderer: ComponentType<FeedRendererProps>;
}

export const FEED_CATALOG: FeedDescriptor[] = [
  // ── agent-caller ──────────────────────────────────────────────────────────
  { id: 'agent-caller/rag', source: 'agent-caller', feed: 'rag', label: 'AI Caller · RAG', icon: Database, kinds: ['rag.query', 'rag.chunks'], Renderer: RagRenderer },
  { id: 'agent-caller/rerank', source: 'agent-caller', feed: 'rerank', label: 'AI Caller · Rerank', icon: ListFilter, kinds: ['rerank.in', 'rerank.out'], Renderer: RagRenderer },
  { id: 'agent-caller/injection', source: 'agent-caller', feed: 'injection', label: 'AI Caller · Context Injection', icon: FileText, kinds: ['context.injection'], Renderer: JsonLogRenderer },
  { id: 'agent-caller/chat', source: 'agent-caller', feed: 'chat', label: 'AI Caller · Chat → model', icon: MessageSquare, kinds: ['chat.injected'], Renderer: JsonLogRenderer },
  { id: 'agent-caller/persona', source: 'agent-caller', feed: 'persona', label: 'AI Caller · Persona (uncensored)', icon: UserCog, kinds: ['persona.loaded', 'persona.grounding'], Renderer: PersonaRenderer },
  { id: 'agent-caller/llm', source: 'agent-caller', feed: 'llm', label: 'AI Caller · LLM', icon: Sparkles, kinds: ['llm.prompt', 'llm.completion'], Renderer: JsonLogRenderer },
  { id: 'agent-caller/tools', source: 'agent-caller', feed: 'tools', label: 'AI Caller · Tools', icon: Wrench, kinds: ['tool.call', 'tool.result'], Renderer: ToolsRenderer },
  { id: 'agent-caller/media', source: 'agent-caller', feed: 'media', label: 'AI Caller · Media', icon: ImageIcon, kinds: ['media.input', 'media.output'], Renderer: MediaRenderer },
  { id: 'agent-caller/log', source: 'agent-caller', feed: 'log', label: 'AI Caller · Log', icon: ScrollText, kinds: ['log'], Renderer: JsonLogRenderer },
  // ── officer-assist ────────────────────────────────────────────────────────
  { id: 'officer-assist/llm', source: 'officer-assist', feed: 'llm', label: 'Officer Assist · LLM', icon: Sparkles, kinds: ['llm.prompt', 'llm.completion'], Renderer: JsonLogRenderer },
  { id: 'officer-assist/rag', source: 'officer-assist', feed: 'rag', label: 'Officer Assist · RAG', icon: Database, kinds: ['rag.query', 'rag.chunks'], Renderer: RagRenderer },
  { id: 'officer-assist/tools', source: 'officer-assist', feed: 'tools', label: 'Officer Assist · Tools', icon: Wrench, kinds: ['tool.call', 'tool.result'], Renderer: ToolsRenderer },
  // The raw conversation_tree visualization is in the officer UI; this feed is
  // what's ACTUALLY injected into the officer model — the formatted strategy
  // guide + the raw strategy snapshot it was derived from — which isn't.
  { id: 'officer-assist/orchestration', source: 'officer-assist', feed: 'orchestration', label: 'Officer Assist · Orchestrator Guide (→ model)', icon: GitBranch, kinds: ['orchestrator.guide'], Renderer: JsonLogRenderer },
  // The pinned state cards actually injected into the Realtime session — the
  // curated turn agenda + strategy + partial-completion (and, later, the
  // entity-changes card). `injected_text` is verbatim what the model holds.
  { id: 'officer-assist/state', source: 'officer-assist', feed: 'state', label: 'Officer Assist · State Cards (→ model)', icon: Layers, kinds: ['next_actions', 'state_changes', 'compaction'], Renderer: JsonLogRenderer },
  // The serialized turn-emission arbiter (§9): enqueued / started / finished /
  // coalesced / dropped / preempt / watchdog_restart transitions.
  { id: 'officer-assist/turn', source: 'officer-assist', feed: 'turn', label: 'Officer Assist · Turn Arbiter', icon: Megaphone, kinds: ['arbiter'], Renderer: JsonLogRenderer },
  { id: 'officer-assist/log', source: 'officer-assist', feed: 'log', label: 'Officer Assist · Log', icon: ScrollText, kinds: ['log'], Renderer: JsonLogRenderer },
  // ── ai-engine (the analysis pipeline's own LLM calls — entity extraction,
  //    suggestions, dedup, answer-check, deep analysis, ...) ────────────────
  { id: 'ai-engine/llm', source: 'ai-engine', feed: 'llm', label: 'AI Engine · LLM (analysis)', icon: Sparkles, kinds: ['llm.prompt', 'llm.completion'], Renderer: JsonLogRenderer },
  { id: 'ai-engine/log', source: 'ai-engine', feed: 'log', label: 'AI Engine · Log', icon: ScrollText, kinds: ['log'], Renderer: JsonLogRenderer },
  // ── agent-backend ─────────────────────────────────────────────────────────
  { id: 'agent-backend/log', source: 'agent-backend', feed: 'log', label: 'Agent Backend · Log', icon: ScrollText, kinds: ['log'], Renderer: JsonLogRenderer },
];

const CATALOG_BY_ID = new Map(FEED_CATALOG.map((d) => [d.id, d]));

/** Resolve a `${source}/${feed}` key to a descriptor; synthesizes a generic
 * one (default renderer, generic label/icon) for anything not in the catalog. */
export function resolveDescriptor(source: string, feed: string): FeedDescriptor {
  const id = `${source}/${feed}`;
  const known = CATALOG_BY_ID.get(id);
  if (known) return known;
  return { id, source, feed, label: `${source} · ${feed}`, icon: Activity, kinds: [], Renderer: JsonLogRenderer };
}

/** Group a flat event list into `${source}/${feed}` buckets (used by the dialog). */
export function bucketByFeed(events: AgentDebugEvent[]): Map<string, AgentDebugEvent[]> {
  const m = new Map<string, AgentDebugEvent[]>();
  for (const ev of events) {
    const key = `${ev.source}/${ev.feed}`;
    const list = m.get(key);
    if (list) list.push(ev);
    else m.set(key, [ev]);
  }
  return m;
}
